import { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Slider,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import ArticleIcon from "@mui/icons-material/Article";
import SchoolIcon from "@mui/icons-material/School";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import CategoryIcon from "@mui/icons-material/Category";
import TuneIcon from "@mui/icons-material/Tune";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { createSelector, Dispatch } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import { setProducts } from "./slice";
import { useSelector } from "react-redux";
import { Product, ProductInquiry } from "../../../lib/types/product";
import { retrieveProducts } from "./selector";
import ProductService from "../../services/ProductService";
import { ProductCollection } from "../../../lib/enums/product.enum";
import { serverApi } from "../../../lib/config";
import { useHistory } from "react-router-dom";
import { CartItem } from "../../../lib/types/search";
import { useGlobals } from "../../hooks/useGlobals";
import { sweetTopSmallSuccessAlert, sweetErrorHandling } from "../../../lib/sweetAlert";

const actionDispatch = (dispatch: Dispatch) => ({
  setProducts: (data: Product[]) => dispatch(setProducts(data)),
});
const productsRetriever = createSelector(retrieveProducts, (products) => ({
  products,
}));

const COLLECTION_CONFIG: {
  value: ProductCollection | "ALL";
  label: string;
  icon: React.ReactNode;
}[] = [
  { value: "ALL", label: "All Books", icon: <LibraryBooksIcon sx={{ fontSize: "1em" }} /> },
  { value: ProductCollection.FICTION, label: "Fiction", icon: <AutoStoriesIcon sx={{ fontSize: "1em" }} /> },
  { value: ProductCollection.NON_FICTION, label: "Non-fiction", icon: <ArticleIcon sx={{ fontSize: "1em" }} /> },
  { value: ProductCollection.ACADEMIC, label: "Academic", icon: <SchoolIcon sx={{ fontSize: "1em" }} /> },
  { value: ProductCollection.KIDS, label: "Kids", icon: <ChildCareIcon sx={{ fontSize: "1em" }} /> },
  { value: ProductCollection.OTHER, label: "Other", icon: <CategoryIcon sx={{ fontSize: "1em" }} /> },
];

const SORT_OPTIONS = [
  { value: "createdAt", label: "Newest Arrivals" },
  { value: "productPrice", label: "Price: Low to High" },
  { value: "productViews", label: "Most Popular" },
];

interface ProductsProps {
  onAdd: (items: CartItem) => void;
}

export default function Products(props: ProductsProps) {
  const { onAdd } = props;
  const { authMember } = useGlobals();
  const dispatch = useDispatch();
  const { setProducts } = useMemo(() => actionDispatch(dispatch), [dispatch]);
  const { products } = useSelector(productsRetriever);
  const history = useHistory();

  const [productSearch, setProductSearch] = useState<ProductInquiry>({
    page: 1,
    limit: 9,
    order: "createdAt",
    productCollection: undefined,
    search: "",
  });
  const [priceRange, setPriceRange] = useState<number[]>([0, 100]);
  const [ratingFilter, setRatingFilter] = useState<number | null>(null);
  const [searchText, setSearchText] = useState("");
  const [refreshCount, setRefreshCount] = useState(0);
  const [hoveredLikeId, setHoveredLikeId] = useState<string | null>(null);

  useEffect(() => {
    const product = new ProductService();
    product
      .getProducts(productSearch)
      .then((data) => setProducts(data))
      .catch((err) => console.log(err));
  }, [productSearch, refreshCount, setProducts]);

  useEffect(() => {
    if (searchText === "") {
      setProductSearch((prev) => ({ ...prev, search: "" }));
    }
  }, [searchText]);

  const searchCollectionHandler = (collection: ProductCollection | "ALL") => {
    setProductSearch({
      ...productSearch,
      page: 1,
      productCollection: collection === "ALL" ? undefined : collection,
    });
  };

  const searchOrderHandler = (order: string) => {
    setProductSearch({ ...productSearch, page: 1, order });
  };

  const PaginationHandler = (e: ChangeEvent<unknown>, value: number) => {
    setProductSearch({ ...productSearch, page: value });
  };

  const removeFilterChip = (type: "collection" | "price") => {
    if (type === "collection") {
      setProductSearch({ ...productSearch, page: 1, productCollection: undefined });
    }
    if (type === "price") {
      setPriceRange([0, 100]);
    }
  };

  const clearAllFilters = () => {
    setProductSearch({
      ...productSearch,
      page: 1,
      productCollection: undefined,
    });
    setPriceRange([0, 100]);
    setRatingFilter(null);
  };

  const filteredProducts = products.filter((p) => {
    const inPrice =
      p.productPrice >= priceRange[0] && (priceRange[1] >= 100 ? true : p.productPrice <= priceRange[1]);
    return inPrice;
  });

  const currentCollectionLabel =
    productSearch.productCollection
      ? COLLECTION_CONFIG.find((c) => c.value === productSearch.productCollection)?.label
      : null;
  const hasPriceFilter = priceRange[0] > 0 || priceRange[1] < 100;
  const priceChipLabel =
    priceRange[0] === 0 && priceRange[1] < 100
      ? `Under $${priceRange[1]}`
      : priceRange[1] >= 100
        ? `Over $${priceRange[0]}`
        : `$${priceRange[0]} - $${priceRange[1]}`;
  const activeFiltersCount =
    (currentCollectionLabel ? 1 : 0) + (hasPriceFilter ? 1 : 0) + (ratingFilter ? 1 : 0);

  const chooseProductHandler = (id: string) => {
    history.push(`/products/${id}`);
  };

  const searchProductHandler = () => {
    const q = searchText.trim() || undefined;
    setProductSearch((prev) => ({ ...prev, page: 1, search: q }));
  };

  const clearSearchHandler = () => {
    setSearchText("");
    setProductSearch((prev) => ({ ...prev, page: 1, search: undefined }));
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    if (value.trim() === "") {
      setProductSearch((prev) => ({ ...prev, page: 1, search: undefined }));
    }
  };

  const likeProductHandler = async (e: React.MouseEvent, productId: string) => {
    e.stopPropagation();
    setHoveredLikeId(null);
    if (!authMember) {
      await sweetErrorHandling({ message: "You are not authenticated, please login first" });
      return;
    }
    try {
      const productService = new ProductService();
      await productService.likeProduct(productId);
      await sweetTopSmallSuccessAlert("Success", 800);
      setRefreshCount((prev) => prev + 1);
    } catch (err) {
      console.log(err);
    }
  };

  const paginationCount =
    products.length >= productSearch.limit
      ? productSearch.page + 1
      : Math.max(productSearch.page, 1);
  const showPagination = products.length > 0 || productSearch.page > 1;

  return (
    <div className="products products-browse">
      <Container maxWidth="lg" sx={{ py: 3 }} className="products-browse-container">
        <Stack direction="row" spacing={3} alignItems="flex-start" className="products-browse-layout">
          {/* Left Sidebar - sticky, stops near top when scrolling */}
          <aside className="products-sidebar">
            <div className="products-sidebar-header">
              <span className="products-sidebar-header-accent" aria-hidden />
              <TuneIcon className="products-sidebar-header-icon" aria-hidden />
              <Typography component="h2" className="products-sidebar-title">
                Refine search
              </Typography>
              {activeFiltersCount > 0 && (
                <>
                  <span className="products-sidebar-badge" aria-label={`${activeFiltersCount} active filters`}>
                    {activeFiltersCount}
                  </span>
                  <button type="button" className="products-sidebar-clear" onClick={clearAllFilters}>
                    Clear
                  </button>
                </>
              )}
            </div>

            <div className="products-sidebar-body">
            <div className="sidebar-filter-group sidebar-filter-group--collections">
            <span className="sidebar-filter-label">Category</span>
            <div className="sidebar-collections" role="group" aria-label="Book categories">
              {COLLECTION_CONFIG.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`sidebar-collection-item ${
                    (item.value === "ALL" && !productSearch.productCollection) ||
                    productSearch.productCollection === item.value
                      ? "active"
                      : ""
                  }`}
                  onClick={() => searchCollectionHandler(item.value)}
                >
                  <span className="sidebar-collection-icon">{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
            </div>

            <div className="sidebar-filter-group sidebar-filter-group--price">
            <div className="sidebar-price-head">
              <span className="sidebar-filter-label">Price</span>
              <span className="sidebar-price-value">
                {priceRange[0] === 0 && priceRange[1] >= 100
                  ? "Any price"
                  : priceRange[0] === 0
                    ? `Up to $${priceRange[1]}`
                    : priceRange[1] >= 100
                      ? `$${priceRange[0]}+`
                      : `$${priceRange[0]} – $${priceRange[1]}`}
              </span>
            </div>
            <Box className="sidebar-price">
              <Slider
                value={priceRange}
                onChange={(_, v) => setPriceRange(v as number[])}
                valueLabelDisplay="off"
                min={0}
                max={100}
                size="small"
                sx={{ color: "#4f46e5", py: 0 }}
              />
              <div className="sidebar-price-limits">
                <span>$0</span>
                <span>$100+</span>
              </div>
            </Box>
            </div>

            <div className="sidebar-filter-group sidebar-filter-group--rating">
            <span className="sidebar-filter-label">Rating</span>
            <div className="sidebar-rating" role="group" aria-label="Minimum rating">
              <button
                type="button"
                className={`sidebar-rating-item ${ratingFilter === 5 ? "active" : ""}`}
                onClick={() => setRatingFilter(ratingFilter === 5 ? null : 5)}
              >
                <span className="sidebar-rating-stars">
                  <StarIcon sx={{ fontSize: 16 }} />
                  <StarIcon sx={{ fontSize: 16 }} />
                  <StarIcon sx={{ fontSize: 16 }} />
                  <StarIcon sx={{ fontSize: 16 }} />
                  <StarIcon sx={{ fontSize: 16 }} />
                </span>
                <span className="sidebar-rating-label">& Up</span>
              </button>
              <button
                type="button"
                className={`sidebar-rating-item ${ratingFilter === 4 ? "active" : ""}`}
                onClick={() => setRatingFilter(ratingFilter === 4 ? null : 4)}
              >
                <span className="sidebar-rating-stars">
                  <StarIcon sx={{ fontSize: 16 }} />
                  <StarIcon sx={{ fontSize: 16 }} />
                  <StarIcon sx={{ fontSize: 16 }} />
                  <StarIcon sx={{ fontSize: 16 }} />
                  <StarBorderIcon sx={{ fontSize: 16 }} />
                </span>
                <span className="sidebar-rating-label">& Up</span>
              </button>
            </div>
            </div>
            </div>
          </aside>

          {/* Main content - scrollable */}
          <main className="products-main">
            <Typography variant="body2" color="text.secondary" className="products-breadcrumbs">
            </Typography>
            <Typography variant="h4" className="products-title">
              Browse Books
            </Typography>
            <Typography variant="body2" color="text.secondary" className="products-description">
              Discover your next favorite read from our extensive collection of bestsellers, classics,
              and new releases.
            </Typography>

            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
              <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" sx={{ flex: 1 }}>
                {currentCollectionLabel && (
                  <Chip
                    label={currentCollectionLabel}
                    onDelete={() => removeFilterChip("collection")}
                    size="small"
                    className="products-filter-chip-collection"
                    sx={{
                      backgroundColor: "#1976d2",
                      color: "#fff",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      "& .MuiChip-deleteIcon": {
                        color: "rgba(255, 255, 255, 0.85)",
                        fontSize: "1.125rem",
                        "&:hover": { color: "#fff" },
                      },
                    }}
                  />
                )}
                {hasPriceFilter && (
                  <Chip
                    label={priceChipLabel}
                    onDelete={() => removeFilterChip("price")}
                    size="small"
                    color="primary"
                  />
                )}
                {activeFiltersCount > 0 && (
                  <Typography
                    variant="body2"
                    component="button"
                    onClick={clearAllFilters}
                    className="clear-filters-btn"
                  >
                    Clear all
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" alignItems="stretch" gap={0} className="products-search-sort-row">
                <div className="products-search-wrap">
                  <TextField
                    size="small"
                    placeholder="Search books..."
                    value={searchText}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && searchProductHandler()}
                    InputProps={{
                      endAdornment: searchText ? (
                        <InputAdornment position="end">
                          <button
                            type="button"
                            onClick={clearSearchHandler}
                            className="products-search-clear-btn"
                            aria-label="Clear search"
                          >
                            <CloseIcon sx={{ fontSize: 18, color: "#757575" }} />
                          </button>
                        </InputAdornment>
                      ) : null,
                    }}
                    className="products-search-input"
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={searchProductHandler}
                    className="products-search-btn"
                  >
                    Search
                  </Button>
                </div>
                <FormControl size="small" sx={{ minWidth: 220 }} className="products-sort-wrap">
                  <Select
                    value={productSearch.order}
                    onChange={(e: SelectChangeEvent) => searchOrderHandler(e.target.value)}
                    displayEmpty
                    className="products-sort-select"
                    renderValue={(v) =>
                      `SORT: ${SORT_OPTIONS.find((o) => o.value === v)?.label ?? "Newest Arrivals"}`
                    }
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>

            <div className="products-grid">
              {filteredProducts.length !== 0 ? (
                filteredProducts.map((product, index) => {
                  const imagePath = `${serverApi}/${product.productImages?.[0] ?? ""}`;
                  const isBestseller = (product.productViews ?? 0) >= 500;
                  const collectionLabel =
                    COLLECTION_CONFIG.find((c) => c.value === product.productCollection)?.label ??
                    product.productCollection;
                  const collectionSlug =
                    (product.productCollection ?? "other").toLowerCase().replace("_", "-");

                  return (
                    <article
                      key={product._id}
                      className={`product-card product-card-book product-card--${collectionSlug}`}
                      onClick={() => chooseProductHandler(product._id)}
                    >
                      <div className="product-card-image-wrap">
                        <div
                          className="product-card-image"
                          style={{ backgroundImage: `url(${imagePath})` }}
                        />
                        <div className={`product-card-badges product-card-badges--${collectionSlug}`}>
                          <span className="product-card-collection-badge">
                            {collectionLabel.toUpperCase()}
                          </span>
                          {isBestseller && <span className="product-badge">Bestseller</span>}
                        </div>
                        <button
                          type="button"
                          className="product-add-hover-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAdd({
                              _id: product._id,
                              quantity: 1,
                              name: product.productName,
                              price: product.productPrice,
                              image: product.productImages?.[0] ?? "",
                            });
                          }}
                        >
                          <AddShoppingCartIcon sx={{ fontSize: 18 }} />
                          Add to basket
                        </button>
                      </div>
                      <Typography variant="subtitle1" className="product-card-title">
                        {product.productName}
                      </Typography>
                      {product.productAuthor && (
                        <Typography variant="body2" color="text.secondary" className="product-card-author">
                          {product.productAuthor}
                        </Typography>
                      )}
                      <div className="product-card-divider" />
                      <div className="product-card-footer">
                        <div className="product-card-footer-left">
                          <Typography variant="body1" className="product-card-price">
                            ${product.productPrice.toFixed(2)}
                          </Typography>
                        </div>
                        <div className="product-card-footer-divider" />
                        <div className="product-card-footer-stats">
                          <span className="product-card-views">
                            <RemoveRedEyeIcon sx={{ fontSize: 22 }} />
                            <span className="product-card-stat-num">{product.productViews ?? 0}</span>
                          </span>
                          
                          <button
                            type="button"
                            className={`product-card-likes ${product?.meLiked?.some((m) => m.myFavorite) ? "product-card-liked" : ""}`}
                            onClick={(e) => likeProductHandler(e, product._id)}
                            onMouseEnter={() => setHoveredLikeId(product._id)}
                            onMouseLeave={() => setHoveredLikeId(null)}
                            onTouchEnd={() => setHoveredLikeId(null)}
                            aria-label={product?.meLiked?.some((m) => m.myFavorite) ? "Unlike" : "Like"}
                          >
                            {product?.meLiked?.some((m) => m.myFavorite) ||
                            (hoveredLikeId === product._id &&
                              typeof window !== "undefined" &&
                              window.matchMedia("(hover: hover) and (pointer: fine)").matches) ? (
                              <FavoriteIcon sx={{ fontSize: 22 }} />
                            ) : (
                              <FavoriteBorderIcon sx={{ fontSize: 22 }} />
                            )}
                            <span className="product-card-stat-num">{product.productLikes ?? 0}</span>
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })
              ) : (
                <Box className="no-data">Products are not available!</Box>
              )}
            </div>

            {showPagination && (
              <Stack className="pagination-section" alignItems="center" sx={{ mt: 4, mb: 4 }}>
                <Pagination
                  count={paginationCount}
                  page={productSearch.page}
                  renderItem={(item) => (
                    <PaginationItem
                      components={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                      {...item}
                    />
                  )}
                  onChange={PaginationHandler}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Stack>
            )}
          </main>
        </Stack>
      </Container>
    </div>
  );
}
